export interface OverrunReason {
  id: string;
  label: string;
  description?: string;
}

const STORAGE_KEY = "timeTracker_overrunReasons";

class OverrunReasonService {
  private reasons: OverrunReason[] = [];

  constructor() {
    this.loadReasons();
  }

  private getDefaultReasons(): OverrunReason[] {
    return [
      {
        id: "increased-scope",
        label: "Increased scope",
        description: "The task requirements expanded beyond the original scope",
      },
      {
        id: "more-complex",
        label: "More complex",
        description: "The task was more complex than initially anticipated",
      },
      {
        id: "changes-requested",
        label: "Changes requested",
        description: "Changes were requested during the task execution",
      },
      {
        id: "blocked",
        label: "Blocked",
        description: "The task was blocked by external dependencies or issues",
      },
      {
        id: "other",
        label: "Other",
        description: "Other reasons not covered by the above categories",
      },
    ];
  }

  private loadReasons(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.reasons = parsed;
          return;
        }
      }
    } catch (error) {
      console.error("Error loading overrun reasons from localStorage:", error);
    }

    // If no stored reasons or error, use defaults
    this.reasons = this.getDefaultReasons();
    this.saveReasons();
  }

  private saveReasons(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.reasons));
    } catch (error) {
      console.error("Error saving overrun reasons to localStorage:", error);
    }
  }

  /**
   * Get all available overrun reasons
   */
  getReasons(): OverrunReason[] {
    return [...this.reasons];
  }

  /**
   * Get a specific reason by ID
   */
  getReasonById(id: string): OverrunReason | undefined {
    return this.reasons.find((reason) => reason.id === id);
  }

  /**
   * Get reason labels by IDs
   */
  getReasonLabelsByIds(ids: string[]): string[] {
    return ids
      .map((id) => this.getReasonById(id))
      .filter(Boolean)
      .map((reason) => reason!.label);
  }

  /**
   * Add a new overrun reason
   */
  addReason(reason: Omit<OverrunReason, "id">): OverrunReason {
    const newReason: OverrunReason = {
      id: this.generateId(reason.label),
      ...reason,
    };
    this.reasons.push(newReason);
    this.saveReasons();
    return newReason;
  }

  /**
   * Update an existing overrun reason
   */
  updateReason(
    id: string,
    updates: Partial<OverrunReason>
  ): OverrunReason | null {
    const index = this.reasons.findIndex((reason) => reason.id === id);
    if (index === -1) return null;

    this.reasons[index] = { ...this.reasons[index], ...updates };
    this.saveReasons();
    return this.reasons[index];
  }

  /**
   * Remove an overrun reason
   */
  removeReason(id: string): boolean {
    const index = this.reasons.findIndex((reason) => reason.id === id);
    if (index === -1) return false;

    this.reasons.splice(index, 1);
    this.saveReasons();
    return true;
  }

  /**
   * Reset to default reasons
   */
  resetToDefaults(): void {
    this.reasons = this.getDefaultReasons();
    this.saveReasons();
  }

  /**
   * Export reasons as JSON
   */
  exportReasons(): string {
    return JSON.stringify(this.reasons, null, 2);
  }

  /**
   * Import reasons from JSON
   */
  importReasons(json: string): boolean {
    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Validate that each item has required fields
        const validReasons = parsed.filter(
          (reason: any) =>
            reason.id &&
            reason.label &&
            typeof reason.id === "string" &&
            typeof reason.label === "string"
        );

        if (validReasons.length === parsed.length) {
          this.reasons = validReasons;
          this.saveReasons();
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error importing overrun reasons:", error);
      return false;
    }
  }

  /**
   * Generate a unique ID based on the label
   */
  private generateId(label: string): string {
    const baseId = label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");

    // Check if ID already exists and add number if needed
    let id = baseId;
    let counter = 1;
    while (this.reasons.some((reason) => reason.id === id)) {
      id = `${baseId}-${counter}`;
      counter++;
    }

    return id;
  }
}

export const overrunReasonService = new OverrunReasonService();
